import { memo, useCallback, useMemo } from 'react';

import { useTrackAgentPillClicked } from '@air/analytics';
import { type ClipType } from '@air/api/types';

import { WorkspaceAgentPillLeadingVisual } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPillLeadingVisual';
import { WorkspaceAgentPreviewCardCloseButton } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPreviewCardCloseButton';
import { WorkspaceAgentReferencePill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentReferencePill';
import { useWorkspaceAgentComposerWorkspaceId } from '~/components/v2/components/workspace/workspace-agent-panel/composer-host/WorkspaceAgentComposerWorkspaceProvider';
import { WorkspaceAgentAssetPreviewCard } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentAssetPreviewCard';
import { useOpenWorkspaceAgentAssetPill } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useOpenWorkspaceAgentAssetPill';
import { useWorkspaceAgentPreviewCardDisclosure } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentPreviewCardDisclosure';
import { usePrivateWorkspaceAgentPillClipCardDetails } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentPillClipCardDetails';
import { usePrivateWorkspaceAgentPillClips } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentPillClips';
import { preserveWorkspaceAgentComposerFocus } from '~/components/v2/components/workspace/workspace-agent-panel/utils/preserveWorkspaceAgentComposerFocus';
import { toWorkspaceAgentAssetPreviewCardContent } from '~/components/v2/components/workspace/workspace-agent-panel/utils/toWorkspaceAgentAssetPreviewCardContent';

export type PrivateWorkspaceAgentComposerAssetPillProps = {
  /** Read off the node's attrs, which carry `null` for a type the pill never learned. */
  assetType: ClipType | null;
  ext: string | null;
  isSelected: boolean;
  label: string;
  onCardClose: () => void;
  onRemove: () => void;
  thumbnailUrl: string | null;
  versionId: string | null;
};

/** Everything downstream of the version, which is a `string` by the time this chip mounts. */
export type PrivateWorkspaceAgentComposerVersionedAssetPillProps = Omit<
  PrivateWorkspaceAgentComposerAssetPillProps,
  'versionId'
> & {
  versionId: string;
};

const PrivateWorkspaceAgentComposerVersionedAssetPill = memo(
  ({
    assetType,
    ext,
    isSelected,
    label,
    onCardClose,
    onRemove,
    thumbnailUrl,
    versionId,
  }: PrivateWorkspaceAgentComposerVersionedAssetPillProps) => {
    const workspaceId = useWorkspaceAgentComposerWorkspaceId();
    const { openAssetClip } = useOpenWorkspaceAgentAssetPill();
    const { trackAgentPillClicked } = useTrackAgentPillClicked();
    const handleCardOpen = useCallback(() => {
      trackAgentPillClicked({ object: 'asset', object_count: 1, object_name: label });
    }, [label, trackAgentPillClicked]);
    const {
      closeCard,
      handleOpenChange,
      isCardOpen,
      navigateFromCard,
      restoreFocusAfterClose,
      suppressEditorFocusRestore,
    } = useWorkspaceAgentPreviewCardDisclosure({
      focusNavigationOrigin: onCardClose,
      // Always: the version is the subject, and this chip is the branch that has one.
      hasSubject: true,
      onCardClose,
      onCardOpen: handleCardOpen,
    });

    const clipIds = useMemo(() => [versionId], [versionId]);

    // Both queries only run while the card is open: the chip itself draws from the node's attrs.
    const { data: clipsById, isLoading } = usePrivateWorkspaceAgentPillClips(
      { clipIds, workspaceId },
      { enabled: isCardOpen },
    );

    const { data: cardDetails } = usePrivateWorkspaceAgentPillClipCardDetails(
      { id: versionId },
      { enabled: isCardOpen },
    );

    const handleOpenAsset = useCallback(() => {
      navigateFromCard(() => openAssetClip(versionId));
    }, [openAssetClip, navigateFromCard, versionId]);

    const clip = clipsById?.get(versionId);
    const resolvedAssetType = clip?.type ?? assetType;
    const resolvedExt = clip?.ext ?? ext;

    return (
      <WorkspaceAgentReferencePill
        card={
          <WorkspaceAgentAssetPreviewCard
            closeButton={
              <WorkspaceAgentPreviewCardCloseButton
                onClose={closeCard}
                onMouseDown={preserveWorkspaceAgentComposerFocus}
              />
            }
            content={
              clip
                ? toWorkspaceAgentAssetPreviewCardContent({
                    boards: cardDetails?.boards ?? null,
                    clip,
                    commentCount: cardDetails?.openCommentCount ?? null,
                  })
                : null
            }
            isLoading={isLoading}
            onBodyMouseDown={preserveWorkspaceAgentComposerFocus}
            onOpen={handleOpenAsset}
            referenceId={versionId}
          />
        }
        isOpen={isCardOpen}
        isSelected={isSelected}
        kind="Asset"
        leadingVisual={
          <WorkspaceAgentPillLeadingVisual
            assetType={resolvedAssetType}
            ext={resolvedExt}
            pillThumbnailTestId="WORKSPACE_AGENT_PILL_THUMBNAIL"
            thumbnailUrl={thumbnailUrl}
          />
        }
        onOpenChange={handleOpenChange}
        onOutsideDismiss={suppressEditorFocusRestore}
        onRemove={onRemove}
        restoreFocusAfterClose={restoreFocusAfterClose}
        surface="composer"
        title={label}
        triggerTestId="WORKSPACE_AGENT_PILL_TRIGGER"
      />
    );
  },
);

PrivateWorkspaceAgentComposerVersionedAssetPill.displayName = 'PrivateWorkspaceAgentComposerVersionedAssetPill';

/**
 * Resolves the version's absence once: a mention minted without one has no clip to fetch and no card
 * to draw, so it renders the chip alone and everything below here treats the version as a `string`.
 */
export const PrivateWorkspaceAgentComposerAssetPill = memo(
  ({
    assetType,
    ext,
    isSelected,
    label,
    onCardClose,
    onRemove,
    thumbnailUrl,
    versionId,
  }: PrivateWorkspaceAgentComposerAssetPillProps) => {
    if (versionId) {
      return (
        <PrivateWorkspaceAgentComposerVersionedAssetPill
          assetType={assetType}
          ext={ext}
          isSelected={isSelected}
          label={label}
          onCardClose={onCardClose}
          onRemove={onRemove}
          thumbnailUrl={thumbnailUrl}
          versionId={versionId}
        />
      );
    }

    return (
      <WorkspaceAgentReferencePill
        card={null}
        isSelected={isSelected}
        kind="Asset"
        leadingVisual={
          <WorkspaceAgentPillLeadingVisual
            assetType={assetType}
            ext={ext}
            pillThumbnailTestId="WORKSPACE_AGENT_PILL_THUMBNAIL"
            thumbnailUrl={thumbnailUrl}
          />
        }
        onRemove={onRemove}
        surface="composer"
        title={label}
        triggerTestId="WORKSPACE_AGENT_PILL_TRIGGER"
      />
    );
  },
);

PrivateWorkspaceAgentComposerAssetPill.displayName = 'PrivateWorkspaceAgentComposerAssetPill';
